import * as React from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";

/**
 * Reusable table toolbar with title, search field, and CRUD action buttons.
 *
 * Props:
 *  - title: string — shown when nothing is selected
 *  - numSelected: number
 *  - searchQuery: string (optional — omit to hide search field)
 *  - setSearchQuery: func (optional)
 *  - handleClickOpen: func — called for Add button (press-style: returns a thunk)
 *  - handleEditClick: func — called for Edit button
 *  - handleDeleteClick: func — called for Delete button
 *  - addButtonLabel: string (default "Create New")
 */
export default function EnhancedTableToolbar({
  title,
  numSelected,
  searchQuery,
  setSearchQuery,
  handleClickOpen,
  handleEditClick,
  handleDeleteClick,
  addButtonLabel = "Create New",
}) {
  const hasSearch = searchQuery !== undefined && setSearchQuery !== undefined;

  return (
    <Toolbar
      sx={[
        { pl: { sm: 2 }, pr: { xs: 1, sm: 1 } },
        numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        },
      ]}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <Typography
            sx={{ flexShrink: 0 }}
            variant="h6"
            id="tableTitle"
            component="div"
          >
            {title}
          </Typography>
          {hasSearch && (
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search..."
              value={searchQuery || ""}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ ml: "auto", width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
      )}

      {numSelected === 1 ? (
        <>
          {handleEditClick && (
            <Tooltip title="Edit">
              <IconButton onClick={handleEditClick}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <IconButton onClick={handleDeleteClick}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <>
          {numSelected > 1 ? (
            <Tooltip title="Delete">
              <IconButton onClick={handleDeleteClick}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={addButtonLabel}>
              <IconButton
                onClick={
                  handleClickOpen
                    ? typeof handleClickOpen === "function" &&
                      handleClickOpen.length === 0
                      ? handleClickOpen
                      : handleClickOpen("paper")
                    : undefined
                }
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  title: PropTypes.string.isRequired,
  numSelected: PropTypes.number.isRequired,
  searchQuery: PropTypes.string,
  setSearchQuery: PropTypes.func,
  handleClickOpen: PropTypes.func,
  handleEditClick: PropTypes.func,
  handleDeleteClick: PropTypes.func.isRequired,
  addButtonLabel: PropTypes.string,
};
